/**
 * Generic multiselect dropdown functionality
 */

/**
 * Multiselect class for managing checkbox-based dropdown selections
 */
class Multiselect {
  /**
   * Create a multiselect instance
   * @param {jQuery} $container - The multiselect container element
   * @param {Object} callbacks - Callback functions
   * @param {Function} callbacks.onChange - Called when selection changes
   * @param {Function} callbacks.onClear - Called when clear button is clicked
   */
  constructor($container, callbacks = {}) {
    this.$container = $container;
    this.callbacks = callbacks;
    this.inputName = this.$container.find('input[type="checkbox"]').attr('name');
    this.$label = this.$container.find('.multiselect-label');

    // Store default label text
    if (!this.$label.data('default-text')) {
      this.$label.data('default-text', this.$label.text());
    }

    this.initializeEventHandlers();
  }

  /**
   * Set or update callbacks
   * @param {Object} callbacks - Callback functions
   * @param {Function} callbacks.onChange - Called when selection changes
   * @param {Function} callbacks.onClear - Called when clear button is clicked
   */
  setCallbacks(callbacks) {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Get all selected option values
   * @returns {Array} Array of selected values
   */
  getSelectedOptions() {
    return jQuery(`input[name="${this.inputName}"]:checked`)
      .map(function getSelectedValues() {
        return jQuery(this).val();
      })
      .get();
  }

  /**
   * Update the multiselect label based on current selections
   */
  updateLabel() {
    const selected = this.getSelectedOptions();
    const defaultText = this.$label.data('default-text');

    if (selected.length === 0) {
      this.$label.text(defaultText);
    } else if (selected.length === 1) {
      const label = jQuery(`input[name="${this.inputName}"][value="${selected[0]}"]`).data('label');
      this.$label.text(label);
    } else {
      this.$label.text(defaultText);
    }
  }

  /**
   * Clear all selections
   */
  clear() {
    this.$container.find('input[type="checkbox"]').prop('checked', false);
    this.updateLabel();

    if (this.callbacks.onClear) {
      this.callbacks.onClear();
    }
  }

  /**
   * Toggle the dropdown open/closed state
   */
  toggle() {
    const isCurrentlyOpen = this.$container.hasClass('is-open');

    // Close all other multiselects
    jQuery('.multiselect-container').not(this.$container).removeClass('is-open');

    // Toggle this one
    this.$container.toggleClass('is-open', !isCurrentlyOpen);
  }

  /**
   * Initialize event handlers for this multiselect instance
   */
  initializeEventHandlers() {
    // Toggle button click
    this.$container.find('.custom-select-opener').on('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggle();
    });

    // Checkbox change
    this.$container.find('input[type="checkbox"]').on('change', () => {
      this.updateLabel();

      if (this.callbacks.onChange) {
        this.callbacks.onChange();
      }
    });

    // Clear button
    this.$container.find('.multiselect-clear').on('click', (e) => {
      e.preventDefault();
      this.clear();
    });

    // Prevent dropdown close when clicking inside panel
    this.$container.find('.multiselect-panel').on('click', (e) => {
      e.stopPropagation();
    });
  }
}

/**
 * Initialize all multiselects on the page
 * @returns {Map} Map of multiselect instances keyed by container ID
 */
function initializeMultiselects() {
  const instances = new Map();

  jQuery('.multiselect-container').each(function initializeMultiselectInstance() {
    const $container = jQuery(this);
    const containerId = $container.attr('id');

    if (containerId) {
      const instance = new Multiselect($container);
      instances.set(containerId, instance);
    }
  });

  // Global: Close all dropdowns when clicking outside
  jQuery(document).on('click', (e) => {
    if (!jQuery(e.target).closest('.multiselect-container').length) {
      jQuery('.multiselect-container').removeClass('is-open');
    }
  });

  return instances;
}

// Create instances Map that will be populated on DOM ready
const multiselectInstances = new Map();

// Auto-initialize when DOM is ready
jQuery(document).ready(() => {
  const instances = initializeMultiselects();
  // Copy all instances to the exported Map
  instances.forEach((instance, key) => {
    multiselectInstances.set(key, instance);
  });
});

export default multiselectInstances;
